import cn from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { type ReactNode, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { getFormatDate } from 'core/classes/helpers/DateHelper'
import { ATTENTION_STATUS_BITS } from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getPlanSignerGroups, type IPlanSignerGroup } from '../../../../lib'
import { getCockpitPlanningStatus, useCockpitPlanningStatuses } from '../../../../model/hooks'
import {
  selectCockpitCommitmentData,
  selectCockpitPartnerData,
  selectCockpitSelectedAttentionStatusOffering,
} from '../../../../model/selectors'
import { useCompletenessOverviewRows } from '../attentionCompletenessOverview'
import { AttentionStatusActions } from '../attentionStatusActions'

import styles from './AttentionStatusCompletenessOverall.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.completeness
const STATUS_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.planningStatus

const EMPTY_VALUE = '--'

/* Truncated rather than rounded, as the completeness detail does: 99.8% is not a finished plan. */
const toPercent = (value: number) => Math.floor(value * 100)

/* Signing and the offering switch act on one programme, and the section to open is one
   programme's, so on `Overall` the corner keeps only what is not tied to a programme. */
const OVERALL_HIDDEN_ACTIONS = ['acknowledge', 'goToSection', 'switchOffOffering'] as const

const countParams = (count: number, total: number) => [
  { key: '{count}', value: String(count) },
  { key: '{total}', value: String(total) },
]

type TChipState = 'signed' | 'missing' | 'neutral'

const SignatureChip = ({ state, children }: { state: TChipState; children: ReactNode }) => (
  <span className={cn(styles.chip, styles[state])}>{children}</span>
)

const SignerState = ({ group }: { group: IPlanSignerGroup }) => {
  if (group.state === 'signed') {
    return (
      <SignatureChip state="signed">
        <Translation
          text={KEYS.signed}
          replaceParams={[{ key: '{date}', value: group.signedDate ?? EMPTY_VALUE }]}
          span
        />
      </SignatureChip>
    )
  }

  return (
    <SignatureChip state={group.state === 'requested' ? 'missing' : 'neutral'}>
      <Translation text={group.statusKey ?? KEYS.notSigned} span />
    </SignatureChip>
  )
}

/**
 * The completeness detail on `Overall`: how many of the partner's plans are complete and signed,
 * one bar per programme under that, and the signers of the programme in focus under the bars. The list
 * counts the status for every programme, so the detail states every programme rather than the
 * first one that raised it. A bar puts its programme in focus; the focus starts on the programme
 * the status was opened for.
 */
export const AttentionStatusCompletenessOverall = () => {
  const rows = useCompletenessOverviewRows()
  const partnerId = useSelector(selectCockpitPartnerData)[0]?.PartnerId ?? ''
  const commitments = useSelector(selectCockpitCommitmentData)
  const raisedFor = useSelector(selectCockpitSelectedAttentionStatusOffering)
  const { completeness, acknowledgment } = getCockpitPlanningStatus(
    useCockpitPlanningStatuses(partnerId),
    ProgramTypes.Overall
  )

  const [picked, setPicked] = useState<string | null>(null)
  const focused =
    rows.find(({ id }) => id === picked) ?? rows.find(({ id }) => id === raisedFor) ?? rows[0]

  const signerGroups = useMemo(
    () =>
      focused
        ? getPlanSignerGroups(commitments, partnerId, focused.id).map((group) => ({
            ...group,
            signedDate: group.signedDate
              ? (getFormatDate(group.signedDate, 'date3') ?? null)
              : null,
          }))
        : [],
    [commitments, focused, partnerId]
  )

  const completeCount = 'count' in completeness ? completeness.count : 0
  const signedCount = 'count' in acknowledgment ? acknowledgment.count : 0
  const total = rows.length
  const share = total ? (completeCount / total) * 100 : 0

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <div className={styles.plate}>
          <div className={styles.total}>
            <span className={styles.totalValue}>
              <Translation
                text={STATUS_KEYS.count}
                replaceParams={countParams(completeCount, total)}
                span
              />
            </span>
            <span className={styles.totalLabel}>
              <Translation text={STATUS_KEYS.complete} span />
            </span>
            <SignatureChip state={total > 0 && signedCount === total ? 'signed' : 'missing'}>
              <Translation
                text={STATUS_KEYS.acknowledgedCount}
                replaceParams={countParams(signedCount, total)}
                span
              />
            </SignatureChip>
          </div>
          <span className={styles.track}>
            <span className={styles.trackFill} style={{ transform: `scaleX(${share / 100})` }} />
          </span>
        </div>

        <div className={styles.programmes}>
          {rows.map(({ id, label, value, fill }) => (
            <button
              key={id}
              type="button"
              className={cn(styles.row, { [styles.rowFocused]: id === focused?.id })}
              aria-pressed={id === focused?.id}
              onClick={() => setPicked(id)}
            >
              <span
                className={styles.fill}
                style={{ transform: `scaleX(${Math.min(1, value ?? 0)})`, background: fill }}
                aria-hidden
              />
              <span className={styles.label}>
                <Translation text={label} span />
              </span>
              <span
                className={cn(styles.value, {
                  [styles.valueShort]: value !== undefined && toPercent(value) < 100,
                })}
              >
                {value === undefined ? EMPTY_VALUE : `${toPercent(value)}%`}
              </span>
            </button>
          ))}
        </div>

        {focused && (
          <div className={styles.signatures}>
            <span className={styles.heading}>
              <Translation text={focused.label} span />
              {' · '}
              <Translation text={KEYS.signaturesTitle} span />
            </span>

            {signerGroups.length === 0 ? (
              <span className={styles.empty}>
                <Translation text={KEYS.noSigners} span />
              </span>
            ) : (
              <div className={styles.signers}>
                {signerGroups.map((group) => {
                  const names = group.names.join(', ')

                  return (
                    <div key={group.typeName} className={styles.signer}>
                      <span className={styles.role}>{group.typeName}</span>
                      <span className={styles.names} title={names}>
                        {names}
                      </span>
                      <SignerState group={group} />
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <AttentionStatusActions
        bitValue={ATTENTION_STATUS_BITS.planned}
        hidden={[...OVERALL_HIDDEN_ACTIONS]}
      />
    </div>
  )
}

AttentionStatusCompletenessOverall.displayName = 'AttentionStatusCompletenessOverall'
